/**
 * Külső hivatkozás → biztonságos abszolút `href` (séma-előtag pótlása).
 *
 * MIÉRT KELL: a felhasználó a weboldal- és link-mezőkbe séma nélkül is ír (`morgens.hu`). Séma nélkül a
 * böngésző RELATÍV útvonalként értelmezné, és az Atlason belülre navigálna — a link némán rossz helyre visz.
 *
 * MIÉRT A KÖZÖS RÉTEGBEN: ugyanez a három soros függvény HÁROM route-fájlban élt külön másolatban (belső
 * jegy-detail, partner jegy-detail, fiók-lista), és a negyedik hívó (a jegy Asana-hivatkozása) miatt került
 * ide — a repo szabálya szerint a 2. másolatnál már a közös rétegben lenne a helye.
 *
 * A `http`/`https` ELŐTAG-ELLENŐRZÉS EGYBEN VÉDELEM is: a séma nélküli bemenet mindig `https://`-t kap, tehát
 * egy `javascript:`/`data:` kezdetű érték sem válik kattintható, végrehajtható hivatkozássá — az is
 * `https://javascript:…` lesz, ami ártalmatlan (és láthatóan hibás) URL.
 */
export function toHref(value: string): string {
  return value.startsWith('http://') || value.startsWith('https://') ? value : `https://${value}`;
}

/**
 * LINK-ALAKÚ-E az érték? — a `toHref` PÁRJA, de ellentétes szereppel: az KÉNYSZERÍT (mindenből linket csinál),
 * ez FELISMER (eldönti, hogy egyáltalán linket akarunk-e csinálni belőle).
 *
 * MIÉRT KELL: vannak SZABAD-SZÖVEGES mezők, amikbe a kollégák hol linket, hol sima szöveget írnak (eszköz:
 * termékkulcs · beszerzés helye · weboldal). A `toHref`-et vakon rájuk engedve egy termékkulcsból
 * (`2VKVN-R7G3F-…`) is `https://2VKVN-R7G3F-…` link lenne — kattintható szemét.
 *
 * A SZABÁLY SZÁNDÉKOSAN SZŰK: csak a `http://`, `https://` és `www.` előtag számít linknek. Ezt MÉRÉS
 * támasztja alá az élő adaton: a három mező 91 kitöltött értékéből 53 valódi link, és MIND
 * `http(s)://`-sel kezdődik; a maradék bolt-név (`Media Markt`, `iSTYLE`) és termékkulcs, amikből egy sem
 * kezdődik így. A szabály tehát nulla téves találattal fogja meg a linkeket.
 *
 * MIÉRT NINCS TLD-HEURISZTIKA: egy séma nélküli `alza.hu` így szöveg marad — tudatos ár. A „van benne pont,
 * tehát domain" minta a `3B3-6WP-7FM (2025.07.10.)` termékkulcsból is linket csinálna, és a téves találat itt
 * rosszabb, mint a kimaradó: a felhasználó egy nem létező címre kattintana.
 *
 * BIZTONSÁG: mivel CSAK a fenti három előtag megy át, a `javascript:`/`data:` séma ide be sem juthat — a
 * `toHref` védelme (séma-pótlás) így nem is kerül próbára ezen az úton.
 *
 * ⚠️ A SÉMA-ELLENŐRZÉS KIS-NAGYBETŰ-ÉRZÉKENY — SZÁNDÉKOSAN, mert a `toHref` szerződése is az (l. a tesztjét).
 * Ha itt lowercase-elnénk, a `HTTPS://morgens.hu` linknek MINŐSÜLNE, a `toHref` viszont nem ismerné el
 * sémának, és `https://HTTPS://morgens.hu` lenne belőle: egy KATTINTHATÓ, TÖRÖTT hivatkozás. Így viszont
 * sima szövegként jelenik meg — láthatóan „nem link", nem pedig némán rossz link.
 * A `www.` ellenőrzése ezzel szemben kis-nagybetű-független: ott a `toHref` amúgy is előtagot tesz, és a
 * host DNS-szinten sem érzékeny a kis-nagybetűre, tehát a `WWW.morgens.hu`-ból ÉRVÉNYES link lesz.
 */
export function isExternalUrl(value: string): boolean {
  const v = value.trim();
  return v.startsWith('http://') || v.startsWith('https://') || v.toLowerCase().startsWith('www.');
}
